import React, { useEffect, useRef, useState } from "react";
import { Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl } from "../../lib/api";
import { PageTitle, TextInput } from "./shared";

const GalleryManager = () => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data: up } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const { data: item } = await api.post("/admin/gallery", { image_url: up.url, caption: "" });
        setItems((l) => [...(l || []), item]);
      }
      toast.success("Image(s) added to gallery");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const saveCaption = async (id, caption) => {
    await api.patch(`/admin/gallery/${id}`, { caption });
    toast.success("Caption saved");
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this image from the gallery?")) return;
    await api.delete(`/admin/gallery/${id}`);
    setItems((l) => l.filter((i) => i.id !== id));
    toast.success("Image removed");
  };

  return (
    <div data-testid="console-gallery-manager">
      <PageTitle kicker="CONTENT" title="GALLERY MANAGER">
        <button
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid="gallery-upload-btn"
          className="inline-flex items-center gap-2 rounded-full bg-magenta text-cream px-6 py-3 text-xs uppercase tracking-[0.16em] font-semibold hover:bg-plum disabled:opacity-50"
        >
          <Upload className="w-4 h-4" /> {busy ? "UPLOADING…" : "UPLOAD IMAGES"}
        </button>
      </PageTitle>
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadFiles(Array.from(e.target.files || []))} data-testid="gallery-file-input" />

      {items === null ? (
        <p className="text-plum-deep/60">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-plum-deep/60" data-testid="gallery-manager-empty">The gallery is empty — upload your first image.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5">
          {items.map((item) => (
            <div key={item.id} className="bg-warmwhite border border-plum-deep/10 rounded-[1.25rem] p-3" data-testid={`gallery-manager-item-${item.id}`}>
              <div className="aspect-square rounded-xl overflow-hidden bg-cream">
                <img src={imgUrl(item.image_url)} alt={item.caption} className="w-full h-full object-cover" />
              </div>
              <div className="flex items-center gap-2 mt-3">
                <TextInput
                  defaultValue={item.caption}
                  placeholder="Caption"
                  onBlur={(e) => e.target.value !== item.caption && saveCaption(item.id, e.target.value)}
                  data-testid={`gallery-caption-${item.id}`}
                />
                <button onClick={() => remove(item.id)} className="text-plum-deep/40 hover:text-red-600 shrink-0" data-testid={`gallery-delete-${item.id}`} aria-label="Delete image">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GalleryManager;
